/**
 * CheckoutCredits — the store's credits that can be used on this cart, in the checkout totals. When
 * checkout opens, the credits that apply are selected for the customer (Brand credits first), each
 * taking the most it can cover (all or nothing; no amount to type). After that only the customer's
 * ticks count: credits apply in the order ticked, each taking what's left after the ones before it, and
 * unticking a credit leaves it unused and doesn't pull in another.
 *
 * Two lists:
 *  - Credits applied: the selected credits taking money off this order, in the order they apply, with
 *    what each takes in red (and its full balance beside the name when only part of it fits). Untick to
 *    move one down.
 *  - Credits available: the rest, with their balances. Tick one to use it. A credit that would have
 *    nothing to cover (the order is already covered, or a Stash House credit with no house-brand
 *    product left) is greyed out, with that reason.
 *
 * The parent works out the amounts (utils/checkoutCredits.js); the server applies what fits when the
 * order is placed.
 */
import { creditLineLabel } from "../../services/creditService.js";
import { formatCurrency } from "../../utils/formatters";

const CreditRow = ({ credit, checked, disabled, onToggle, children, note }) => {
  const checkboxId = `credit-${credit.id}`;
  return (
    <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
      <div className="form-check mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          id={checkboxId}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(credit, e.target.checked)}
        />
        <label className={`form-check-label${disabled ? " text-muted" : ""}`} htmlFor={checkboxId}>
          {creditLineLabel(credit)}
          {note && <span className="text-muted ms-1">{note}</span>}
        </label>
      </div>
      {children}
    </div>
  );
};

const CheckoutCredits = ({ credits, selectedIds, amounts, maxFor, onToggle, disabled }) => {
  const isApplied = (credit) => selectedIds.has(credit.id) && (amounts[credit.id] ?? 0) > 0;
  const applied = credits.filter(isApplied);
  const available = credits.filter((credit) => !isApplied(credit));

  return (
    <div className="border rounded p-3">
      <div className="fw-semibold mb-2">Credits applied</div>
      {applied.length === 0 && <div className="small text-muted mb-1">None — tick a credit below to use it.</div>}
      {applied.map((credit) => {
        const amount = amounts[credit.id] ?? 0;
        const partial = amount < credit.remainingAmount;
        return (
          <CreditRow
            key={credit.id}
            credit={credit}
            checked
            disabled={disabled}
            onToggle={onToggle}
            note={partial ? `(${formatCurrency(credit.remainingAmount)} credit)` : null}
          >
            <span className="fw-medium text-danger">({formatCurrency(amount)})</span>
          </CreditRow>
        );
      })}

      {available.length > 0 && (
        <>
          <div className="fw-semibold mt-3 mb-2">Credits available</div>
          {available.map((credit) => {
            // Selected but taking nothing, or would take nothing if ticked: nothing left for it to cover.
            const nothingToCover = selectedIds.has(credit.id) || maxFor(credit) <= 0;
            return (
              <div key={credit.id}>
                <CreditRow
                  credit={credit}
                  checked={false}
                  disabled={disabled || nothingToCover}
                  onToggle={onToggle}
                >
                  <span className={`fw-medium${nothingToCover ? " text-muted" : ""}`}>
                    {formatCurrency(credit.remainingAmount)}
                  </span>
                </CreditRow>
                {nothingToCover && (
                  <div className="small text-muted ms-4 mb-1">Nothing left on this order for it to cover.</div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default CheckoutCredits;
